import Box from '@mui/material/Box';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { ErrorState } from '../../../media/shared/ErrorState.js';
import { NoData } from '../../../media/shared/NoData.js';
import { rangeLabel } from '../../data/format.js';
import { type CreativeDetailScope, useSingleCreativePerformance } from '../data/api.js';
import { SectionHeader } from '../shared/SectionHeader.js';
import { SectionSkeleton, TILE_GRID_SX } from '../shared/SectionSkeleton.js';
import { BenchmarkedMetricTile } from './BenchmarkedMetricTile.js';

const VISIBLE_TILE_COUNT = 6;

interface PerformanceTilesProps {
  scope: CreativeDetailScope;
  creativeId: string;
  kpis: string[];
  selectedMetricNames?: string[];
  onMetricSelect?: (metricName: string) => void;
}

export function PerformanceTiles({
  scope,
  creativeId,
  kpis,
  selectedMetricNames = [],
  onMetricSelect,
}: PerformanceTilesProps): ReactNode {
  const performance = useSingleCreativePerformance({ ...scope, creativeId, kpis }, true);
  const [expanded, setExpanded] = useState(false);

  const metrics = performance.data?.metrics ?? [];
  const visibleMetrics = expanded ? metrics : metrics.slice(0, VISIBLE_TILE_COUNT);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <SectionHeader title="Performance" caption={rangeLabel(scope.dateRange)} />
      </Box>
      {performance.isError ? (
        <ErrorState />
      ) : performance.data === undefined ? (
        <SectionSkeleton variant="tiles" tileCount={Math.min(kpis.length, VISIBLE_TILE_COUNT)} />
      ) : metrics.length === 0 ? (
        <NoData message="No performance metrics for this creative." />
      ) : (
        <>
          <Box sx={{ ...TILE_GRID_SX, mx: -2 }}>
            {visibleMetrics.map((metric) => (
              <BenchmarkedMetricTile
                key={metric.name}
                metric={metric}
                currencySymbol={performance.data.currencySymbol}
                selected={selectedMetricNames.includes(metric.name)}
                {...(onMetricSelect === undefined ? {} : { onSelect: onMetricSelect })}
              />
            ))}
          </Box>
          {metrics.length > VISIBLE_TILE_COUNT && (
            <Box
              component="button"
              type="button"
              onClick={() => setExpanded((current) => !current)}
              sx={{
                display: 'block',
                bgcolor: 'transparent',
                border: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: 'primary.main',
                cursor: 'pointer',
                p: 0,
                mt: 2,
                transition: (theme) =>
                  theme.transitions.create('opacity', {
                    duration: theme.transitions.duration.shortest,
                  }),
                '&:hover': { opacity: 0.7 },
              }}
            >
              {expanded ? 'View less' : 'View more'}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
